import { useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Text from 'components/shared/Text'
import Box from 'components/shared/Box'
import InfoTooltip from 'components/shared/InfoTooltip'
import PersonTypeRadio from 'components/shared/RadioButtons/PersonTypeRadio'
import Grid from 'components/shared/Grid'
import { useApp } from 'store/steps'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useListTicketPersonsQuery } from 'store/api-slice/hooks'
import { updateTicketPersonType } from 'store/ticket-slice'
import { Person } from 'store/api-slice/types'
import Loader from 'components/shared/Loader'
import { useAutoScroll } from 'utils/hooks'
import { useTickets } from 'store/ticket-slice/hooks'
import { DefaultTheme } from 'styled-components'
import { setIsErrorModalOpened } from 'store/general-slice'

interface PersonTypeProps {
    index: number
    boxId: string
    theme?: DefaultTheme
}

const PersonType = ({ index, boxId, theme }: PersonTypeProps) => {
    const { removeTicketFromCart, isTicketInCart } = useTickets(boxId)
    const { calculateScroll, animatePendingScroll, scrollIndex } =
        useAutoScroll()
    const { language } = useApp()
    const { data, isFetching, isError } = useListTicketPersonsQuery({
        _locale: language,
    })
    const { persons } = data || {}
    const dispatch = useAppDispatch()
    const { ticketBoxes, areTicketsUpdating } = useAppSelector(
        (state) => state.ticket
    )
    const currentTicket = ticketBoxes.find((box) => box.boxId === boxId)
    const { person: currentPerson } = currentTicket || {}
    const { formatMessage } = useIntl()

    useEffect(() => {
        if (isFetching || !persons) return
        animatePendingScroll(`tickets-${index}`)
    }, [isFetching, scrollIndex])

    const handleSetPerson = (key: Person) => {
        if (areTicketsUpdating) return
        if (isTicketInCart) {
            removeTicketFromCart()
        }
        dispatch(updateTicketPersonType({ person: key, boxId }))

        calculateScroll({
            trigerringSection: 'tickets',
            trigerringAction: 'modify',
            index,
            parameter: 'person',
        })
    }

    useEffect(() => {
        if ((!isFetching && !persons) || isError) {
            dispatch(setIsErrorModalOpened(true))
        }
    }, [persons, isFetching, isError])

    return (
        <Box gap={[16, 24]} isFullWidth={true} id={`tickets-${index}-person`}>
            <Box direction="row" align="center" gap={8}>
                <Text variant="heading3" color="grayscale_900" theme={theme}>
                    <FormattedMessage
                        id="tickets.person_section_caption"
                        defaultMessage="Wybierz typ gościa"
                    />
                </Text>
                <InfoTooltip
                    message={formatMessage({
                        id: 'upsells.tooltip.person_type',
                        defaultMessage:
                            'Pamiętaj, aby zabrać ze sobą dokument tożsamości i dokument potwierdzający uprawnienia do ulgi (karta seniora, karta ciąży lub orzeczenie o niepełnosprawności).',
                    })}
                />
            </Box>
            <Grid gap={[8, 16]} columns={[1, 4]}>
                {isFetching ? (
                    <Loader />
                ) : (
                    <>
                        {persons?.map((person) => {
                            const {
                                description,
                                displayName,
                                key: optionKey,
                                title,
                            } = person

                            if (!optionKey || !title) return null

                            return (
                                <PersonTypeRadio
                                    key={optionKey}
                                    id="person"
                                    isChecked={optionKey === currentPerson}
                                    label={displayName || title}
                                    onClick={() =>
                                        areTicketsUpdating
                                            ? null
                                            : handleSetPerson(optionKey)
                                    }
                                    option={title}
                                    displayName={displayName || title}
                                    info={description}
                                    theme={theme}
                                    testId={`ticket-${index}-select-person-${optionKey}`}
                                />
                            )
                        })}
                    </>
                )}
            </Grid>
        </Box>
    )
}

export default PersonType
